import { z } from 'zod'

// Run validation schemas
export const createRunSchema = z.object({
  title: z.string()
    .min(1, 'Title is required')
    .max(200, 'Title must be less than 200 characters'),
  date: z.string().optional().refine((date) => {
    if (!date) return true
    return !isNaN(Date.parse(date))
  }, 'Invalid date format'),
  notes: z.string()
    .max(1000, 'Notes must be less than 1000 characters')
    .optional()
})

export const updateRunSchema = createRunSchema.partial()

// Segment validation schemas
export const createSegmentSchema = z.object({
  label: z.string()
    .max(100, 'Label must be less than 100 characters')
    .optional(),
  distance_km: z.number()
    .min(0.10, 'Distance must be at least 0.10 km')
    .max(1000, 'Distance must be less than 1000 km'),
  elev_gain_m: z.number()
    .min(0, 'Elevation gain cannot be negative')
    .max(10000, 'Elevation gain seems too high'),
  elev_loss_m: z.number()
    .min(0, 'Elevation loss cannot be negative')
    .max(10000, 'Elevation loss seems too high'),
  planned_stop_min: z.number()
    .min(0, 'Planned stop cannot be negative')
    .max(300, 'Planned stop seems too long')
    .default(0)
})

export const updateSegmentSchema = createSegmentSchema.partial()

// Aid station validation schemas
export const createAidStationSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  at_distance_km: z.number()
    .min(0, 'Distance cannot be negative'),
  available: z.array(z.string()).optional(),
  notes: z.string()
    .max(500, 'Notes must be less than 500 characters')
    .optional(),
  planned_stop_min: z.number()
    .min(0, 'Planned stop cannot be negative')
    .max(300, 'Planned stop seems too long')
    .default(0)
})

export const updateAidStationSchema = createAidStationSchema.partial()

// Plan item validation schemas
export const createPlanItemSchema = z.object({
  type: z.union([z.literal('snack'), z.literal('drink'), z.literal('note')]),
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  carbs_g: z.number()
    .min(0, 'Carbs cannot be negative')
    .max(200, 'Carbs amount seems too high')
    .optional(),
  fluids_ml: z.number()
    .min(0, 'Fluids cannot be negative')
    .max(2000, 'Fluid amount seems too high')
    .optional(),
  sodium_mg: z.number()
    .min(0, 'Sodium cannot be negative')
    .max(5000, 'Sodium amount seems too high')
    .optional(),
  caffeine_mg: z.number()
    .min(0, 'Caffeine cannot be negative')
    .max(500, 'Caffeine amount seems too high')
    .optional(),
  note: z.string()
    .max(500, 'Note must be less than 500 characters')
    .optional()
}).refine((data) => {
  // Snacks should have carbs
  if (data.type === 'snack' && (!data.carbs_g || data.carbs_g === 0)) {
    return false
  }
  // Drinks should have fluids
  if (data.type === 'drink' && (!data.fluids_ml || data.fluids_ml === 0)) {
    return false
  }
  return true
}, {
  message: 'Snacks must have carbs, drinks must have fluids',
  path: ['type']
})

// For update operations, we need to make fields optional before applying refine()
const basePlanItemSchema = z.object({
  type: z.union([z.literal('snack'), z.literal('drink'), z.literal('note')]),
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  carbs_g: z.number()
    .min(0, 'Carbs cannot be negative')
    .max(200, 'Carbs amount seems too high')
    .optional(),
  fluids_ml: z.number()
    .min(0, 'Fluids cannot be negative')
    .max(2000, 'Fluid amount seems too high')
    .optional(),
  sodium_mg: z.number()
    .min(0, 'Sodium cannot be negative')
    .max(5000, 'Sodium amount seems too high')
    .optional(),
  caffeine_mg: z.number()
    .min(0, 'Caffeine cannot be negative')
    .max(500, 'Caffeine amount seems too high')
    .optional(),
  note: z.string()
    .max(500, 'Note must be less than 500 characters')
    .optional()
})

export const updatePlanItemSchema = basePlanItemSchema.partial().refine((data) => {
  // Only validate if type is present
  if (!data.type) return true

  // Snacks should have carbs
  if (data.type === 'snack' && (!data.carbs_g || data.carbs_g === 0)) {
    return false
  }
  // Drinks should have fluids
  if (data.type === 'drink' && (!data.fluids_ml || data.fluids_ml === 0)) {
    return false
  }
  return true
}, {
  message: 'Snacks must have carbs, drinks must have fluids',
  path: ['type']
})

// Calibration profile validation
export const createCalibrationProfileSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  beta0_base_pace_min_per_km: z.number()
    .min(3, 'Base pace must be at least 3 min/km')
    .max(15, 'Base pace must be less than 15 min/km'),
  beta1_uphill_min_per_m_per_km: z.number()
    .min(0, 'Uphill factor cannot be negative')
    .max(0.1, 'Uphill factor seems too high'),
  beta2_downhill_min_per_m_per_km: z.number()
    .max(0, 'Downhill factor must be zero or negative'),
  beta3_fatigue_min_per_km: z.number()
    .min(0, 'Fatigue factor cannot be negative')
    .max(0.01, 'Fatigue factor seems too high'),
  use_fatigue: z.boolean().default(false),
  target_carbs_g_per_h_low: z.number().min(0).max(200).default(60),
  target_carbs_g_per_h_high: z.number().min(0).max(200).default(90),
  target_fluids_ml_per_h_low: z.number().min(0).max(2000).default(500),
  target_fluids_ml_per_h_high: z.number().min(0).max(2000).default(750),
  target_sodium_mg_per_h_low: z.number().min(0).max(2000).default(300),
  target_sodium_mg_per_h_high: z.number().min(0).max(2000).default(800)
}).refine((data) => {
  return data.target_carbs_g_per_h_low <= data.target_carbs_g_per_h_high
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_carbs_g_per_h_high']
}).refine((data) => {
  return data.target_fluids_ml_per_h_low <= data.target_fluids_ml_per_h_high
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_fluids_ml_per_h_high']
}).refine((data) => {
  return data.target_sodium_mg_per_h_low <= data.target_sodium_mg_per_h_high
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_sodium_mg_per_h_high']
})

// For update operations, we need to make fields optional before applying refine() chains
const baseCalibrationProfileSchema = z.object({
  name: z.string()
    .min(1, 'Name is required')
    .max(100, 'Name must be less than 100 characters'),
  beta0_base_pace_min_per_km: z.number()
    .min(3, 'Base pace must be at least 3 min/km')
    .max(15, 'Base pace must be less than 15 min/km'),
  beta1_uphill_min_per_m_per_km: z.number()
    .min(0, 'Uphill factor cannot be negative')
    .max(0.1, 'Uphill factor seems too high'),
  beta2_downhill_min_per_m_per_km: z.number()
    .max(0, 'Downhill factor must be zero or negative'),
  beta3_fatigue_min_per_km: z.number()
    .min(0, 'Fatigue factor cannot be negative')
    .max(0.01, 'Fatigue factor seems too high'),
  use_fatigue: z.boolean().default(false),
  target_carbs_g_per_h_low: z.number().min(0).max(200).default(60),
  target_carbs_g_per_h_high: z.number().min(0).max(200).default(90),
  target_fluids_ml_per_h_low: z.number().min(0).max(2000).default(500),
  target_fluids_ml_per_h_high: z.number().min(0).max(2000).default(750),
  target_sodium_mg_per_h_low: z.number().min(0).max(2000).default(300),
  target_sodium_mg_per_h_high: z.number().min(0).max(2000).default(800)
})

export const updateCalibrationProfileSchema = baseCalibrationProfileSchema.partial().refine((data) => {
  // Only validate if both values are present
  if (data.target_carbs_g_per_h_low !== undefined && data.target_carbs_g_per_h_high !== undefined) {
    return data.target_carbs_g_per_h_low <= data.target_carbs_g_per_h_high
  }
  return true
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_carbs_g_per_h_high']
}).refine((data) => {
  // Only validate if both values are present
  if (data.target_fluids_ml_per_h_low !== undefined && data.target_fluids_ml_per_h_high !== undefined) {
    return data.target_fluids_ml_per_h_low <= data.target_fluids_ml_per_h_high
  }
  return true
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_fluids_ml_per_h_high']
}).refine((data) => {
  // Only validate if both values are present
  if (data.target_sodium_mg_per_h_low !== undefined && data.target_sodium_mg_per_h_high !== undefined) {
    return data.target_sodium_mg_per_h_low <= data.target_sodium_mg_per_h_high
  }
  return true
}, {
  message: 'Low target must be less than or equal to high target',
  path: ['target_sodium_mg_per_h_high']
})

// Custom validation functions for complex business rules
export function validateAidStationDistance(
  distance: number,
  totalCourseDistance: number,
  existingStations: { id: string; at_distance_km: number }[] = [],
  excludeId?: string
): { isValid: boolean; message?: string } {
  if (distance < 0) {
    return { isValid: false, message: 'Distance cannot be negative' }
  }
  
  if (distance > totalCourseDistance) {
    return { 
      isValid: false, 
      message: `Distance cannot exceed course total (${totalCourseDistance.toFixed(2)} km)` 
    }
  }
  
  // Check for stations too close together (within 0.1 km)
  const others = existingStations.filter(station => station.id !== excludeId)
  const tooClose = others.find(station => Math.abs(station.at_distance_km - distance) < 0.1)
  
  if (tooClose) {
    return { 
      isValid: false, 
      message: 'Aid stations must be at least 0.1 km apart' 
    }
  }
  
  return { isValid: true }
}

export function validateNutritionBalance(
  planItems: Array<{ type: string; carbs_g?: number; fluids_ml?: number; sodium_mg?: number }>,
  estimatedTimeHours: number,
  targets: {
    carbs_g_per_h: { low: number; high: number }
    fluids_ml_per_h: { low: number; high: number }
    sodium_mg_per_h: { low: number; high: number }
  }
): { warnings: string[]; errors: string[] } {
  const warnings: string[] = []
  const errors: string[] = []
  
  if (estimatedTimeHours === 0) return { warnings, errors }
  
  const totalCarbs = planItems.reduce((sum, item) => sum + (item.carbs_g || 0), 0)
  const totalFluids = planItems.reduce((sum, item) => sum + (item.fluids_ml || 0), 0)
  const totalSodium = planItems.reduce((sum, item) => sum + (item.sodium_mg || 0), 0)
  
  const carbsPerHour = totalCarbs / estimatedTimeHours
  const fluidsPerHour = totalFluids / estimatedTimeHours
  const sodiumPerHour = totalSodium / estimatedTimeHours
  
  // Carbohydrate validation
  if (carbsPerHour < targets.carbs_g_per_h.low) {
    warnings.push(`Carbohydrate intake (${carbsPerHour.toFixed(1)}g/h) is below recommended range`)
  } else if (carbsPerHour > targets.carbs_g_per_h.high) {
    warnings.push(`Carbohydrate intake (${carbsPerHour.toFixed(1)}g/h) may cause GI distress`)
  }
  
  // Fluid validation
  if (fluidsPerHour < targets.fluids_ml_per_h.low) {
    warnings.push(`Fluid intake (${fluidsPerHour.toFixed(0)}ml/h) may lead to dehydration`)
  } else if (fluidsPerHour > targets.fluids_ml_per_h.high) {
    warnings.push(`High fluid intake (${fluidsPerHour.toFixed(0)}ml/h) may cause hyponatremia`)
  }
  
  // Sodium validation
  if (sodiumPerHour < targets.sodium_mg_per_h.low) {
    warnings.push(`Sodium intake (${sodiumPerHour.toFixed(0)}mg/h) may be insufficient`)
  } else if (sodiumPerHour > targets.sodium_mg_per_h.high) {
    warnings.push(`Sodium intake (${sodiumPerHour.toFixed(0)}mg/h) may be excessive`)
  }
  
  // Check for drinks without carbs on long efforts
  if (estimatedTimeHours > 2) {
    const drinksWithoutCarbs = planItems.filter(
      item => item.type === 'drink' && (!item.carbs_g || item.carbs_g === 0)
    )
    if (drinksWithoutCarbs.length > 0) {
      warnings.push('Consider adding carbs to drinks for efforts longer than 2 hours')
    }
  }
  
  // Check for no nutrition on long efforts
  if (estimatedTimeHours > 1 && totalCarbs === 0) {
    errors.push('Consider adding nutrition for efforts longer than 1 hour')
  }
  
  return { warnings, errors }
}

// Form validation result types
export type ValidationResult<T> = {
  success: boolean
  data?: T
  errors?: Record<string, string[]>
}

// Helper function to format Zod errors for form display
export function formatValidationErrors(error: z.ZodError): Record<string, string[]> {
  const errors: Record<string, string[]> = {}
  
  for (const issue of error.issues) {
    const path = issue.path.join('.')
    if (!errors[path]) {
      errors[path] = []
    }
    errors[path].push(issue.message)
  }
  
  return errors
}

// Validation hook for forms
export function useFormValidation<T>(schema: z.ZodSchema<T>) {
  return {
    validate: (data: unknown): ValidationResult<T> => {
      try {
        const validatedData = schema.parse(data)
        return {
          success: true,
          data: validatedData
        }
      } catch (error) {
        if (error instanceof z.ZodError) {
          return {
            success: false,
            errors: formatValidationErrors(error)
          }
        }
        return {
          success: false,
          errors: { general: ['Validation failed'] }
        }
      }
    }
  }
}